"use client";

import React from "react";
import { ArrowLeftIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Shared chrome for the `ask` and `ask_open` stepper cards. Both tools render the same
 * counter + dot-row header and the same Back / [middle slot] / Next-or-Submit footer; the
 * differences live in the per-step body (option list vs. textarea) and the validation gate.
 *
 * The shell is intentionally dumb — it doesn't own step state, doesn't fetch translations,
 * doesn't decide what counts as "answered". Callers pass already-localised strings and a
 * dot-state per question so the visual contract stays consistent across tools without
 * coupling either card to the other's semantics.
 */

export type DotState = "active" | "done" | "pending";

export type StepperDot = {
  key: string;
  ariaLabel: string;
  state: DotState;
  onClick?: () => void;
};

export const StepperHeader: React.FC<{
  /** Set to false to hide the entire header (single-question case). */
  show: boolean;
  /** Already-localised "Question X of Y" text. */
  counterLabel: string;
  dots: StepperDot[];
  disabled?: boolean;
}> = ({ show, counterLabel, dots, disabled = false }) => {
  if (!show) return null;
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>{counterLabel}</span>
      <div className="flex gap-1">
        {dots.map(d => (
          <button
            key={d.key}
            type="button"
            aria-label={d.ariaLabel}
            disabled={disabled || !d.onClick}
            onClick={d.onClick}
            className={cn(
              "h-1.5 w-6 rounded-full transition-colors",
              d.state === "active" ? "bg-blue-500"
                : d.state === "done" ? "bg-emerald-500"
                : "bg-muted",
            )}
          />
        ))}
      </div>
    </div>
  );
};

export type StepperFooterButton = {
  label: string;
  onClick: () => void;
  disabled: boolean;
};

export const StepperFooter: React.FC<{
  back: StepperFooterButton;
  /** Optional centre slot — currently used by ask_open's per-step Skip. Pass null to omit. */
  middle?: React.ReactNode;
  primary: StepperFooterButton;
  /** Set true to render Submit semantics (kept identical visually — purely intent for callers). */
  isLast?: boolean;
}> = ({ back, middle, primary }) => (
  <div className="flex items-center justify-between gap-2 border-t pt-3">
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={back.onClick}
      disabled={back.disabled}
      className="gap-1"
    >
      <ArrowLeftIcon className="size-3.5" />
      {back.label}
    </Button>
    <div className="flex items-center gap-2">
      {middle}
      <Button size="sm" disabled={primary.disabled} onClick={primary.onClick}>
        {primary.label}
      </Button>
    </div>
  </div>
);
